import {
  Background,
  Controls,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type ReactFlowInstance
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './recording-canvas.css';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useNavigate } from '@tanstack/react-router';
import { useIsMutating } from '@tanstack/react-query';
import { useProcess } from '@/api/queries/useProcesses';
import { useProcessDraft, type ProcessDraftQueryStatus } from '@/api/queries/useProcessDraft';
import {
  useAddDraftEventAssignments,
  usePublishDraft,
  useRemoveDraftEventAssignments,
  useUpdateDraftEvent
} from '@/api/mutations/useProcessDraft';
import { processDraftKeys } from '@/api/query-keys';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useOrgMembersForLookup } from '@/api/queries/useOrgMembers';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useCelebrationStore } from '@/stores/celebration-store';
import { useEditorSuggestionSource } from '@/pages/processes/editor/components/useEditorSuggestionSource';
import { useDraftPublishHandlers } from '@/pages/processes/editor/draft/useDraftPublishHandlers';
import { PublishErrorDialog } from '@/pages/processes/editor/draft/panels/PublishErrorDialog';
import {
  buildEventsById,
  buildStepsById
} from '@/pages/processes/editor/draft/publish/publishErrorFormatting';
import { draftEditorEventsOf } from '@/pages/processes/editor/draft/draftEvents';

// Import node components
import StartEventNode from './nodes/StartEventNode';
import SuccessorCardNode from './nodes/SuccessorCardNode';
import {
  getStartEventTypes,
  InputFormDialog,
  Page,
  PageContent,
  PageFooter,
  type StartEventContent
} from '@bliks-io/ui';
import { ProcessRecordingBottomBar } from './components/ProcessRecordingBottomBar';
import { getStartDisabledReasons } from './utils/getStartDisabledReasons';
import {
  findStartAttachment,
  planStartAssigneeChange,
  startAssigneeBuckets
} from './utils/startAssignees';
import { ProcessDetailsDialog } from '@/components/processes/ProcessDetailsDialog';
import { EndlessProcessLoader } from '@/pages/processes/components/endless-process/EndlessProcessLoader';
import type { PublishDraftResponse, PublishValidationError, StepInput } from '@bliks-io/contracts';
import type { SuccessorUpdateData } from './types';
const nodeTypes = {
  startEvent: StartEventNode,
  successorCard: SuccessorCardNode
} as const;

// Published or discarded from another tab shows up as 'not-found' outside our
// own publish; only the loader creates drafts.
function readDraft(data: ProcessDraftQueryStatus | undefined, isPublishing: boolean) {
  if (isPublishing || !data) return { draft: null, draftGoneElsewhere: false };
  if (data === 'not-found') return { draft: null, draftGoneElsewhere: true };
  return { draft: data, draftGoneElsewhere: false };
}

export function StartRecordingPage() {
  const { t } = useTranslation();
  const { processId, slug } = useParams({
    from: '/$slug/processes/recording/start-recording/$processId'
  });
  const navigate = useNavigate();

  const { data: processData } = useProcess(processId);
  const process = processData?.process;
  const draftQuery = useProcessDraft(processId);
  const publishDraft = usePublishDraft(processId);
  // Publish removes the draft cache and this page stays mounted until the
  // navigation lands — derive nothing from a draft that is being destroyed.
  const isPublishing = publishDraft.isPending || publishDraft.isSuccess;
  const { draft, draftGoneElsewhere } = readDraft(draftQuery.data, isPublishing);

  const { startEvent, startId, startAssignments, hasStartAttachedNode } = useMemo(
    () => findStartAttachment(draft),
    [draft]
  );
  const entitySuggestions = draft?.entitySuggestions;

  useDocumentTitle('pageTitles.startRecording', { name: process?.name || 'Loading...' });
  // Lookup variant resolves soft-deleted person assignees.
  const { data: orgMembers } = useOrgMembersForLookup();
  const { anchorId: suggestionAnchorId, allowCreate } = useEditorSuggestionSource(startId);

  // Mutations
  const updateDraftEvent = useUpdateDraftEvent(processId);
  const addStartAssignments = useAddDraftEventAssignments(processId);
  const removeStartAssignments = useRemoveDraftEventAssignments(processId);
  const pendingMutations = useIsMutating({ mutationKey: processDraftKeys.mutations(processId) });

  // Local state
  const [showEventModal, setShowEventModal] = useState(false);
  const [showProcessDetailsModal, setShowProcessDetailsModal] = useState(false);

  // The card lists assignees of start-attached draft assignments; a
  // start-attached step lives in the editor.
  const successorValue = useMemo(() => startAssigneeBuckets(startAssignments), [startAssignments]);

  // Validation — reasons drives both the tooltip and the disabled state, so
  // they can never disagree (mirrors the step-recording finish button).
  const startDisabledReasons = useMemo(
    () => getStartDisabledReasons(startEvent, hasStartAttachedNode),
    [startEvent, hasStartAttachedNode]
  );

  const handlePublished = useCallback(
    (result: PublishDraftResponse) => {
      const celebration = useCelebrationStore.getState();
      celebration.recordCompletion();
      // A failed invite gets its own warning toast; the card's "invited" would contradict it.
      if (process && result.invitesFailed.length === 0) {
        celebration.queueRecordingStarted(process.name);
      }
      navigate({ to: '/$slug/my-tasks', params: { slug } });
    },
    [navigate, slug, process]
  );

  // A process this page can reach has no live steps, so `draft-stale` cannot
  // fire — the conflict half of the handlers stays unused.
  const [publishError, setPublishError] = useState<PublishValidationError | null>(null);
  const { handlePublish } = useDraftPublishHandlers({
    onPublished: handlePublished,
    publishDraft,
    loadTimeLiveConflict: null,
    onValidationRejected: setPublishError
  });

  const stepsById = useMemo(() => buildStepsById(draft?.steps ?? []), [draft?.steps]);
  const eventsById = useMemo(() => buildEventsById(draftEditorEventsOf(draft)), [draft]);

  // Handle save start event — the draft's start arm writes through to the
  // live start node.
  const handleSaveStartEvent = useCallback(
    async (eventData: StartEventContent) => {
      if (!startId) return;

      try {
        await updateDraftEvent.mutateAsync({
          eventId: startId,
          patch: {
            eventType: eventData.eventType,
            name: eventData.name,
            description: eventData.description
          }
        });

        setShowEventModal(false);
      } catch (error) {
        toastApiError(error, 'errors.unknown');
      }
    },
    [startId, updateDraftEvent]
  );

  // The card emits whole per-bucket lists; only the difference to the
  // rendered value is written, and the server decides delete vs detach for
  // a removal. `successorStatus` is dropped: the starter card never offers
  // unclear/none.
  const handleSuccessorChange = useCallback(
    async (update: SuccessorUpdateData) => {
      if (!startId) return;
      const { additions, removals } = planStartAssigneeChange(update, successorValue);

      try {
        if (additions) {
          await addStartAssignments.mutateAsync({ eventId: startId, body: additions });
        }
        if (removals) {
          await removeStartAssignments.mutateAsync({ eventId: startId, body: removals });
        }
      } catch (error) {
        toastApiError(error, 'errors.unknown');
      }
    },
    [startId, successorValue, addStartAssignments, removeStartAssignments]
  );

  // ReactFlow nodes
  const initialNodes: Node[] = useMemo(() => {
    return [
      {
        id: 'inset-s-1',
        type: 'startEvent',
        position: { x: 100, y: 200 },
        data: {
          label:
            startEvent?.name && startEvent.name.trim() !== ''
              ? startEvent.name
              : t('viralRecording.labels.startEvent'),
          description: startEvent?.description,
          eventType: startEvent?.eventType,
          // The label above falls back to a placeholder when unnamed, so the
          // node can't infer configured-ness from it — pass it explicitly for
          // the create-vs-edit CTA (a configured none-type event must not
          // re-show "Create Start Event").
          hasName: !!startEvent?.name?.trim(),
          onCreateStartEvent: () => setShowEventModal(true)
        }
      },
      {
        id: 'successor-card-1',
        type: 'successorCard',
        position: { x: 550, y: 96 },
        data: {
          users: orgMembers || [],
          value: successorValue,
          onChange: handleSuccessorChange,
          submitted: false,
          // Suggestions raised here are draft-scoped and anchor to the draft
          // start row (`sourceRecordingId`); `processId` keeps driving the
          // "for this process" picker bucket.
          processId,
          draftProcessId: processId,
          allowCreate,
          startEventNodeId: suggestionAnchorId,
          type: 'starter', // Initial step cannot be Unclear or None
          entitySuggestions
        }
      }
    ];
  }, [
    startEvent,
    successorValue,
    entitySuggestions,
    orgMembers,
    handleSuccessorChange,
    processId,
    allowCreate,
    suggestionAnchorId,
    t
  ]);

  const initialEdges: Edge[] = useMemo(() => {
    return [
      {
        id: 'e-start-successor-card',
        source: 'inset-s-1',
        target: 'successor-card-1',
        type: 'step',
        animated: false,
        style: {
          stroke: '#d1d5db',
          strokeWidth: 2
        }
      }
    ];
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  // Use a stable content signature (no functions) to decide when to update nodes
  const nodesContentSignature = useMemo(() => {
    return JSON.stringify({
      startEvent: {
        name: startEvent?.name || '',
        description: startEvent?.description || '',
        eventType: startEvent?.eventType || ''
      },
      successors: successorValue,
      // Proposed names render from the suggestion docs — re-sync nodes when a
      // referenced suggestion appears (e.g. just created from the picker).
      suggestions: (entitySuggestions ?? []).map((suggestion) => ({
        id: suggestion._id,
        name: suggestion.payload.name
      })),
      usersCount: orgMembers?.length || 0,
      allowCreate,
      suggestionAnchorId
    });
  }, [
    startEvent?.name,
    startEvent?.description,
    startEvent?.eventType,
    successorValue,
    entitySuggestions,
    orgMembers,
    allowCreate,
    suggestionAnchorId
  ]);

  const previousSignatureRef = useRef<string>('');

  useEffect(
    function syncNodesOnContentChange() {
      if (!draft) return;
      if (previousSignatureRef.current !== nodesContentSignature) {
        previousSignatureRef.current = nodesContentSignature;
        setNodes(initialNodes);
      }
    },
    [nodesContentSignature, initialNodes, draft, setNodes]
  );

  // Keep a ref to the React Flow instance for viewport control
  const rfInstanceRef = useRef<ReactFlowInstance | null>(null);

  useEffect(
    function refitViewportOnNodesChange() {
      if (nodes.length > 0 && rfInstanceRef.current) {
        // Let DOM paint before fitting
        requestAnimationFrame(() => {
          try {
            rfInstanceRef.current?.fitView({ padding: 0.6 });
          } catch {
            // ignore if not ready yet
          }
        });
      }
    },
    [nodes, edges]
  );

  // The event-details dialog edits the configured start event; the minted-
  // empty node (eventType 'none', empty name) opens it blank, mirroring the
  // legacy absent-initialEvent state.
  const startEventDialogInput = useMemo(() => {
    if (!startEvent) return undefined;
    const isUnconfigured =
      !startEvent.name &&
      !startEvent.description &&
      (!startEvent.eventType || startEvent.eventType === 'none');
    if (isUnconfigured) return undefined;
    return {
      eventType: startEvent.eventType,
      name: startEvent.name,
      description: startEvent.description
    };
  }, [startEvent]);

  if (draftGoneElsewhere) {
    return <Navigate to="/$slug/processes/all-processes/$processId" params={{ slug, processId }} />;
  }

  if (!process || (!draft && !isPublishing)) {
    return <EndlessProcessLoader messageKey="viralRecording.status.loadingProcessData" />;
  }

  return (
    <Page>
      <PageContent className="p-0">
        {/* Node callbacks write to the draft, so nothing on the canvas may be operated while it publishes. */}
        <div className="h-full bg-bg-5" inert={isPublishing}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            proOptions={{ hideAttribution: true }}
            className="recording-canvas"
            fitView
            fitViewOptions={{ padding: 0.6 }}
            zoomOnScroll
            preventScrolling={false}
            panOnScroll
            panOnDrag
            nodesDraggable={false}
            nodesConnectable
            // Read-only canvas: React Flow's default Backspace-delete would
            // remove selected nodes client-side only (nothing persists), so
            // keyboard deletion is disabled outright.
            deleteKeyCode={null}
            snapToGrid
            snapGrid={[20, 20]}
            onInit={(instance) => {
              rfInstanceRef.current = instance;
            }}
          >
            <Background color="#aaa" gap={16} size={1} />
            <Controls position="top-right" />
          </ReactFlow>
        </div>
      </PageContent>
      <PageFooter>
        <ProcessRecordingBottomBar
          onBack={() => window.history.back()}
          processName={process?.name}
          onViewProcessDetails={() => setShowProcessDetailsModal(true)}
          isSaving={pendingMutations > 0}
          primaryAction={{
            label: t('viralRecording.buttons.startRecording'),
            onClick: () => void handlePublish(),
            loading: isPublishing,
            disabled: startDisabledReasons.length > 0 || pendingMutations > 0,
            disabledReasons: startDisabledReasons,
            disabledTitle: 'recording.startDisabled.title'
          }}
          showAssignButton={false}
        />
      </PageFooter>

      {/* Input Form Dialog - Initial Event Mode */}
      <InputFormDialog
        open={showEventModal}
        onOpenChange={setShowEventModal}
        onSubmit={handleSaveStartEvent as (data: StepInput | StartEventContent) => void}
        mode="initial-event"
        eventTypes={getStartEventTypes()}
        input={startEventDialogInput}
        isSubmitting={updateDraftEvent.isPending}
      />

      {publishError && (
        <PublishErrorDialog
          error={publishError}
          stepsById={stepsById}
          eventsById={eventsById}
          onClose={() => setPublishError(null)}
          onGoToStep={() =>
            navigate({ to: '/$slug/processes/editor/$processId', params: { slug, processId } })
          }
        />
      )}

      {/* Process Details Modal */}
      <ProcessDetailsDialog
        open={showProcessDetailsModal}
        onOpenChange={setShowProcessDetailsModal}
        process={process}
      />
    </Page>
  );
}
